/**
 * Client-side .xlsx → text, so a data-room financials workbook can be extracted without a
 * parsing library or any server-side spreadsheet code (the Worker never sees the bytes; it only
 * receives the text, exactly like the deck path in pdf.ts).
 *
 * An .xlsx is a ZIP of XML parts. We unzip with fflate (already a dependency for PPTX) and pull:
 *   xl/workbook.xml            sheet names, in tab order
 *   xl/_rels/workbook.xml.rels sheet name → worksheet part
 *   xl/sharedStrings.xml       the string pool cells point into
 *   xl/worksheets/sheetN.xml   the cells themselves
 *
 * Output is one pipe-separated row per spreadsheet row, per sheet, with empty columns preserved —
 * column alignment is what lets the extractor tell FY24 from FY25, so gaps must not collapse.
 */
import { unzipSync } from 'fflate';
import { decodeEntities } from './xml.js';

const MAX_ROWS_PER_SHEET = 500;
const MAX_CHARS = 200_000;

/** "B7" → 1 (zero-based column index). */
function colIdx(ref: string): number {
  const letters = /^([A-Z]+)/.exec(ref.toUpperCase())?.[1] ?? '';
  let n = 0;
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64);
  return Math.max(0, n - 1);
}

/** All the text inside an XML fragment's <t> elements, entity-decoded. */
const textRuns = (xml: string): string =>
  [...xml.matchAll(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g)].map((m) => decodeEntities(m[1] ?? '')).join('');

function sharedStrings(xml: string | undefined): string[] {
  if (!xml) return [];
  return [...xml.matchAll(/<si>([\s\S]*?)<\/si>/g)].map((m) => textRuns(m[1] ?? '').replace(/\s+/g, ' ').trim());
}

/** Sheet parts in workbook tab order, with their display names. */
function sheetOrder(files: Record<string, Uint8Array>, dec: TextDecoder): { name: string; path: string }[] {
  const parts = Object.keys(files).filter((k) => /^xl\/worksheets\/sheet\d+\.xml$/.test(k))
    .sort((a, b) => Number(a.match(/(\d+)/)?.[1] ?? 0) - Number(b.match(/(\d+)/)?.[1] ?? 0));
  const wb = files['xl/workbook.xml'] ? dec.decode(files['xl/workbook.xml']) : '';
  const rels = files['xl/_rels/workbook.xml.rels'] ? dec.decode(files['xl/_rels/workbook.xml.rels']) : '';
  const target = new Map<string, string>();
  for (const m of rels.matchAll(/<Relationship\b[^>]*Id="([^"]+)"[^>]*Target="([^"]+)"/g)) {
    const path = `xl/${(m[2] ?? '').replace(/^\/?xl\//, '').replace(/^\.\//, '')}`;
    target.set(m[1] ?? '', path);
  }
  const named: { name: string; path: string }[] = [];
  for (const m of wb.matchAll(/<sheet\b[^>]*?name="([^"]*)"[^>]*?r:id="([^"]+)"[^>]*\/?>/g)) {
    const path = target.get(m[2] ?? '');
    if (path && files[path]) named.push({ name: decodeEntities(m[1] ?? '').trim() || path, path });
  }
  // Fall back to numeric part order when the workbook/rels parts are missing or unreadable.
  return named.length ? named : parts.map((p, i) => ({ name: `Sheet${i + 1}`, path: p }));
}

/** One worksheet part → pipe-separated rows (blank rows dropped, column positions kept). */
function sheetRows(xml: string, strings: string[]): string[] {
  const out: string[] = [];
  for (const rowM of xml.matchAll(/<row\b[^>]*>([\s\S]*?)<\/row>/g)) {
    if (out.length >= MAX_ROWS_PER_SHEET) break;
    const cells: string[] = [];
    for (const cM of (rowM[1] ?? '').matchAll(/<c\b([^>]*)>([\s\S]*?)<\/c>/g)) {
      const attrs = cM[1] ?? '', inner = cM[2] ?? '';
      const ref = /r="([A-Z]+\d+)"/i.exec(attrs)?.[1] ?? '';
      const type = /t="([^"]+)"/.exec(attrs)?.[1] ?? 'n';
      const v = /<v>([\s\S]*?)<\/v>/.exec(inner)?.[1] ?? '';
      let text = '';
      if (type === 's') text = strings[Number(v)] ?? '';
      else if (type === 'inlineStr') text = textRuns(inner).replace(/\s+/g, ' ').trim();
      else if (type === 'str' || type === 'e') text = decodeEntities(v).trim();
      else text = v.trim(); // numeric / boolean / date serial — kept as printed in the file
      const i = ref ? colIdx(ref) : cells.length;
      while (cells.length < i) cells.push('');
      cells[i] = text;
    }
    if (cells.some((x) => x !== '')) out.push(cells.join(' | '));
  }
  return out;
}

/**
 * Extract a workbook's cells as text, sheet by sheet. Throws only when the file is not a
 * readable .xlsx (a legacy binary .xls, or a corrupt zip) — the caller surfaces that message.
 */
export async function extractXlsxText(file: File): Promise<string> {
  let files: Record<string, Uint8Array>;
  try {
    files = unzipSync(new Uint8Array(await file.arrayBuffer()));
  } catch {
    throw new Error('Could not read this workbook. If it is a legacy .xls file, re-save it as .xlsx or export the sheet as CSV/PDF.');
  }
  const dec = new TextDecoder();
  if (!Object.keys(files).some((k) => k.startsWith('xl/'))) {
    throw new Error('That ZIP is not an Excel workbook — upload the .xlsx, or export the sheet as CSV/PDF.');
  }
  const strings = sharedStrings(files['xl/sharedStrings.xml'] ? dec.decode(files['xl/sharedStrings.xml']!) : undefined);
  const parts: string[] = [];
  let chars = 0;
  for (const { name, path } of sheetOrder(files, dec)) {
    const rows = sheetRows(dec.decode(files[path]!), strings);
    if (!rows.length) continue;
    const block = `=== Sheet: ${name} ===\n${rows.join('\n')}`;
    chars += block.length;
    parts.push(block);
    if (chars >= MAX_CHARS) break;
  }
  return parts.join('\n\n');
}

/**
 * Read any financials file the analyst picks into text: PDF and PPTX via pdf.js/fflate,
 * XLSX via the workbook parser above, CSV/TSV/TXT as-is.
 */
export async function extractFinancialsText(file: File, deckText: (f: File) => Promise<string>): Promise<string> {
  const n = file.name.toLowerCase();
  if (n.endsWith('.xlsx') || n.endsWith('.xlsm') || file.type.includes('spreadsheetml')) return extractXlsxText(file);
  if (n.endsWith('.xls')) throw new Error('Legacy .xls files are not readable in the browser — re-save as .xlsx, or export the sheet as CSV/PDF.');
  if (n.endsWith('.pdf') || n.endsWith('.pptx')) return deckText(file);
  return file.text();
}
